import { ActionTree } from 'vuex';
import RootState from '@/store/root_state';
import { StudyCalendarEventState } from './state';
import { CalendarEvent, CalendarEventType } from '@/models/undergrad/calendar-event.model';
import { UndergradLive } from '@/models/undergrad/undergrad-live.model';
import { GradeWorthyActivities } from '@/store/undergrad/gradeWorthyActivities/state';
import {
  DisciplineSubmissionDeadline,
  DisciplineSubmissionDeadlineType,
} from '@/models/undergrad/disciplines-submission-deadline.model';
import { isGreaterThan } from '@/helpers/date';
import moment from 'moment';

function getDisciplineSubmissionDeadLines(
  gradeWorthyActivities: GradeWorthyActivities[],
): DisciplineSubmissionDeadline[] {
  const disciplineSubmissionDeadlines: DisciplineSubmissionDeadline[] = [];
  const now = moment().utc(true).toISOString();

  gradeWorthyActivities
    .flatMap((item) => item.disciplines)
    .filter((discipline) => discipline.exercisesDeadlines)
    .forEach((discipline) => {
      [
        new Date(discipline.exercisesDeadlines!.firstDueDate),
        new Date(discipline.exercisesDeadlines!.secondDueDate),
      ].forEach((dueDate, i) => {
        const stage = i + 1;

        if (!isGreaterThan(now, dueDate.toISOString())) {
          disciplineSubmissionDeadlines.push({
            id: `${discipline.id}_${stage}`,
            discipline: { id: discipline.id, name: discipline.name },
            stage,
            dueDate,
            type: DisciplineSubmissionDeadlineType.PENDING,
          });
        }
      });
    });

  return disciplineSubmissionDeadlines;
}

const actions: ActionTree<StudyCalendarEventState, RootState> = {
  PULL_STUDY_CALENDAR_EVENTS({ commit, getters }): void {
    commit('SET_IS_LOADING_STUDY_CALENDAR_EVENT_DATA', { isLoading: true });

    const calendarEvents: CalendarEvent[] = [];

    const lives: UndergradLive[] = [
      ...getters.undergradCourseLives,
      ...getters.homeUndergradDisciplinesLives,
    ].filter((live, i, array) => i === array.findIndex((l) => l.id === live.id));

    calendarEvents.push(
      ...lives.map(
        (live: UndergradLive): CalendarEvent => ({
          content: live,
          type: CalendarEventType.LIVE,
          dueDate: live.transmissionStartDate,
        }),
      ),
    );

    const disciplineSubmissionDeadLines = getDisciplineSubmissionDeadLines(
      getters.getGradeWorthyActivities,
    );

    calendarEvents.push(
      ...disciplineSubmissionDeadLines.map(
        (disciplineSubmissionDeadline: DisciplineSubmissionDeadline): CalendarEvent => ({
          content: disciplineSubmissionDeadline,
          type: CalendarEventType.SUBMISSION_DEADLINE,
          dueDate: disciplineSubmissionDeadline.dueDate,
        }),
      ),
    );

    commit('SET_STUDY_CALENDAR_EVENTS', {
      studyCalendarEvents: calendarEvents.sort(
        (source, target) => source.dueDate.getTime() - target.dueDate.getTime(),
      ),
    });
    commit('SET_IS_LOADING_STUDY_CALENDAR_EVENT_DATA', { isLoading: false });
  },
};

export default actions;
